import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/api';

/** `POST /api/licenses/:id/devices/:activationId/reactivate` — revive one seat. */
async function reactivateSeat(
  licenseId: string,
  activationId: string,
  reason: string | null
): Promise<{ rebound: boolean }> {
  const response = await apiFetch(
    `/licenses/${encodeURIComponent(licenseId)}/devices/${encodeURIComponent(activationId)}/reactivate`,
    { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) }
  );
  const body = (await response.json().catch(() => ({}))) as { rebound?: boolean };
  return { rebound: body.rebound === true };
}

interface ReactivateDeviceDialogProps {
  /** Target seat (`activations.id`); `null` keeps the dialog closed. */
  activationId: string | null;
  /** Licence the seat belongs to — the device list refreshed on success. */
  licenseId: string;
  /**
   * The seat is a revoked tombstone (its hardware anchor was handed to a replacing
   * installation): reactivation reverses that — the current live device is revoked.
   */
  revoked?: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Bring a released device seat back from the dashboard. Confirming revives the seat
 * within the licence pool — an ordinary deactivated seat is re-occupied, while a
 * revoked tombstone recovers its real hardware anchor (the replacing installation is
 * tombstoned in turn). No destructive styling: this restores, it does not remove.
 */
export default function ReactivateDeviceDialog({
  activationId,
  licenseId,
  revoked = false,
  onOpenChange,
}: ReactivateDeviceDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      reactivateSeat(licenseId, activationId!, reason.trim() === '' ? null : reason.trim()),
    onSuccess: ({ rebound }) => {
      toast.success(
        rebound
          ? t('licenses.detail.devices.reactivate.reboundToast')
          : t('licenses.detail.devices.reactivate.toastSuccess')
      );
      queryClient.invalidateQueries({ queryKey: ['license', 'devices', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['license', 'detail', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['license', 'audit', licenseId] });
      setReason('');
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t('licenses.detail.devices.reactivate.toastError'));
    },
  });

  const handleClose = (open: boolean) => {
    if (!open && mutation.isPending) return;
    if (!open) setReason('');
    onOpenChange(open);
  };

  return (
    <Dialog open={activationId !== null} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('licenses.detail.devices.reactivate.title')}</DialogTitle>
          <DialogDescription>
            {t('licenses.detail.devices.reactivate.description')}
          </DialogDescription>
        </DialogHeader>

        {revoked ? (
          <p className="text-[13px] text-status-revoked" data-testid="reactivate-rebound-warning">
            {t('licenses.detail.devices.reactivate.reboundWarning')}
          </p>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="reactivate-reason">
            {t('licenses.detail.devices.reactivate.reason')}
          </Label>
          <Input
            id="reactivate-reason"
            autoComplete="off"
            maxLength={512}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="reactivate-reason"
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={mutation.isPending}
          >
            {t('licenses.detail.devices.reactivate.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            data-testid="reactivate-confirm"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('licenses.detail.devices.reactivate.reactivating')}
              </>
            ) : (
              t('licenses.detail.devices.reactivate.confirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
